import clsx from 'clsx'
import { CurrencyField } from 'components/core-v2/molecules/fields/currency-field'
import React, { useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { formatCurrencyFull } from 'utilities/currency'
import { formatUnits } from 'utilities/number'

import { CURRENCY } from '../../../const'
import { ConfirmedOrder, isBuyOrder } from '../orders-detail/orders-types'
import classes from './order-correct-price-modal.module.scss'

export interface PriceCorrectionGridProps {
  order: ConfirmedOrder
}

const priceCorrectionId = 'priceCorrection'

const formatNumber = (value: number, decimalPlaces = 0) => {
  const scale = decimalPlaces === 0 ? 1 : 10 ** decimalPlaces
  return Math.round((value + Number.EPSILON) * scale) / scale
}

export const PriceCorrectionGrid = ({ order }: PriceCorrectionGridProps) => {
  const correction = useWatch({ name: priceCorrectionId })

  const data = useMemo(() => {
    const value = correction?.numerical
    const price = order.executionPrice
    if (isBuyOrder(order)) {
      const units = formatNumber(order.value / (order.executionPrice / 100), 6)
      const total = order.value
      const priceDifference = value ? formatNumber(price - value, 4) : 0
      const unitCorrection = value ? formatNumber(total / (value / 100), 6) : 0
      const unitDifference = value ? formatNumber(units - unitCorrection, 6) : 0
      return { price, units, total, priceDifference, unitCorrection, unitDifference }
    }

    const units = Math.abs(order.shares)
    const total = (order.executionPrice / 100) * units
    const priceDifference = value ? formatNumber(price - value, 4) : 0
    const totalCorrection = value ? formatNumber(units * (value / 100), 6) : 0
    const totalDifference = value ? formatNumber(total - totalCorrection, 6) : 0
    return { price, units, total, priceDifference, totalCorrection, totalDifference }
  }, [correction?.numerical, order])

  return (
    <div className={classes.gridContainer}>
      <div className={clsx(classes.emptySpace)} />
      <div className={clsx(classes.currentPrice, classes.heading)}>
        <strong>Current price</strong>
      </div>
      <div
        className={clsx(classes.priceCorrection, classes.heading, classes.priceCorrectionColumn)}
      >
        <div>
          <strong>Price correction</strong>
        </div>
        <div className={classes.subHeading}>
          If the total number of units is greater that the expected total number of units
        </div>
      </div>
      <div className={clsx(classes.difference, classes.heading)}>
        <strong>Difference</strong>
      </div>
      <div className={clsx(classes.price, classes.row)}>
        <strong>Price per unit</strong>
      </div>
      <div className={clsx(classes.row)}>
        {formatCurrencyFull(data.price, CURRENCY, null, true)}
      </div>
      <div className={clsx(classes.row, classes.priceCorrectionColumn)}>
        <CurrencyField
          decimalPlaces={4}
          id={priceCorrectionId}
          symbol="ZAc"
          validators={{
            validate: {
              nonNegative: ({ numerical: value }) => value > 0
            }
          }}
        />
      </div>
      <div
        className={clsx(classes.row, {
          [classes.negativeImpact]:
            correction && isBuyOrder(order) ? data.priceDifference < 0 : data.priceDifference > 0
        })}
      >
        {correction
          ? formatCurrencyFull(Math.abs(data.priceDifference), CURRENCY, null, true)
          : '-'}
      </div>

      <div className={clsx(classes.number, classes.row)}>
        <strong>Number of units</strong>
      </div>
      <div className={clsx(classes.row)}>{formatUnits(data.units)}</div>
      <div className={clsx(classes.row, classes.priceCorrectionColumn)}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {!isBuyOrder(order)
          ? formatUnits(data.units)
          : data.unitCorrection
          ? formatUnits(data.unitCorrection)
          : '-'}
      </div>
      <div
        className={clsx(classes.row, {
          [classes.negativeImpact]: correction && !!data.unitDifference && data.unitDifference > 0
        })}
      >
        {correction && data.unitDifference ? Math.abs(data.unitDifference) : '-'}
      </div>

      <div className={clsx(classes.total, classes.row)}>
        <strong>Total amount</strong>
      </div>
      <div className={clsx(classes.row)}>{formatCurrencyFull(data.total, CURRENCY)}</div>
      <div className={clsx(classes.row, classes.priceCorrectionColumn)}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {isBuyOrder(order)
          ? formatCurrencyFull(data.total, CURRENCY)
          : data.totalCorrection
          ? formatCurrencyFull(data.totalCorrection, CURRENCY)
          : '-'}
      </div>
      <div
        className={clsx(classes.row, {
          [classes.negativeImpact]: correction && data.totalDifference > 0
        })}
      >
        {correction && data.totalDifference
          ? formatCurrencyFull(Math.abs(data.totalDifference), CURRENCY)
          : '-'}
      </div>
    </div>
  )
}
